import { Box, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';

import { parseOutput, type ParsedOutput } from './parseTenantOutput';

const TERRAFORM_OUTPUT_CMD = 'terraform output -json';

interface PasteTenantOutputDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onParse: (parsed: ParsedOutput) => void;
  connectMethod: 'terraform' | 'bash';
}

export const PasteTenantOutputDialog = ({
  isOpen,
  onClose,
  onParse,
  connectMethod,
}: PasteTenantOutputDialogProps) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleParse = () => {
    setError(null);

    if (!input.trim()) {
      setError('Please paste the output before parsing.');
      return;
    }

    const parsed = parseOutput(input, connectMethod);

    if (!parsed) {
      setError(
        connectMethod === 'terraform'
          ? `Invalid JSON. Make sure you ran: \`${TERRAFORM_OUTPUT_CMD}\``
          : "Invalid JSON. Make sure you're pasting the JSON output from the script."
      );
      return;
    }

    if (Object.keys(parsed).length === 0) {
      setError(
        'No valid fields found in the output. Make sure the output contains the expected field names.'
      );
      return;
    }

    onParse(parsed);
    handleClose();
  };

  const handleClose = () => {
    setInput('');
    setError(null);
    onClose();
  };

  const instructionSubtitle =
    connectMethod === 'terraform' ? (
      <Typography variant='body1' component='span'>
        Run{' '}
        <Box
          component='code'
          sx={{
            backgroundColor: 'var(--mui-palette-background-secondary)',
            padding: '2px 6px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '0.9em',
          }}
        >
          {TERRAFORM_OUTPUT_CMD}
        </Box>{' '}
        and paste the output here:
      </Typography>
    ) : (
      'Paste the JSON output from the script here:'
    );

  return (
    <MainDialog
      isOpen={isOpen}
      isLoading={false}
      header={{
        title: 'Paste Output',
        subtitle: instructionSubtitle,
        fullWidthSubtitle: false,
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: 'Parse & Apply',
          onClick: handleParse,
          disabled: !input.trim(),
          'data-testid': 'parse-output-button',
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': 'cancel-paste-output-button',
        },
      }}
    >
      <Stack gap='16px' paddingX='40px'>
        <TextField
          multiline
          rows={8}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
          }}
          placeholder={
            connectMethod === 'terraform'
              ? '{"backup_app_service_principal_id":{"value":"..."},"restore_app_application_id":{"value":"..."},...}'
              : '{"backup_app_service_principal_id":"...","restore_app_application_id":"...",...}'
          }
          sx={{
            '& .MuiInputBase-root': {
              fontFamily: 'monospace',
              fontSize: '14px',
            },
          }}
          error={!!error}
          helperText={error}
        />
      </Stack>
    </MainDialog>
  );
};
